import React, { useCallback, useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import { adminApi } from "@/lib/api";
import { tooltipPercentFormatter, legendPercentFormatter } from "@/lib/charts";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

// Data-viz palette (Pass 15). Tuned for distinguishability while staying
// luxury-appropriate — charcoal anchor + four jewel-tone accents that
// read clearly side-by-side without competing with the brand chrome
// (#5C5C5C body / #5C5C5C button text, which stays untouched).
const CHART_COLORS = ["#3D3D3D", "#B8860B", "#2F5E3D", "#7A2E2E", "#4A6FA5"];

function SummaryCard({ label, value, testid }) {
  return (
    <div className="scw-card" style={{ padding: "24px 24px" }} data-testid={testid}>
      <p className="scw-eyebrow" style={{ marginBottom: 10 }}>{label}</p>
      <p style={{ fontSize: 36, margin: 0, fontWeight: 500 }}>{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setErr("");
    setLoading(true);
    try {
      const r = await adminApi.dashboard();
      setData(r.data);
    } catch (e) {
      // 503 means the backend partially failed (Sheets API blip). Suggest retry.
      const detail = e?.response?.data?.detail;
      setErr(detail || e?.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <AdminLayout>{"Loading\u2026"}</AdminLayout>;
  if (err && !data) {
    return (
      <AdminLayout>
        <div style={{ padding: 24, border: "1px solid var(--scw-line)", background: "#fcf8f4" }} data-testid="dash-load-error">
          <p className="scw-eyebrow" style={{ marginBottom: 6, color: "#a13b3b" }}>Dashboard temporarily unavailable</p>
          <p style={{ margin: "0 0 12px 0", fontSize: 13 }}>{err}</p>
          <button type="button" className="scw-btn scw-btn-ghost" style={{ padding: "6px 14px" }} onClick={load} data-testid="dash-retry-btn">
            Retry
          </button>
        </div>
      </AdminLayout>
    );
  }

  const s = data.summary;
  return (
    <AdminLayout>
      <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Overview</p>
      <h1 style={{ fontSize: 28, margin: "0 0 28px 0" }}>Dashboard</h1>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 32 }}>
        <SummaryCard label="Leads today" value={s.today} testid="dash-leads-today" />
        <SummaryCard label="This week" value={s.week} testid="dash-leads-week" />
        <SummaryCard label="This month" value={s.month} testid="dash-leads-month" />
        <SummaryCard label="Appointments next 7 days" value={s.appointments_upcoming} testid="dash-appts-upcoming" />
      </div>

      {/* Pass 46 — Review-request queue rendered at the BOTTOM of the
          dashboard, below Upcoming appointments. See block at end of
          this layout. */}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8, marginBottom: 32 }} data-testid="dash-duty-week">
        {(data.duty_week || []).map((d) => {
          const isClosed = d.is_off || d.is_sunday;
          // Sundays are never staffed; on a public holiday the admin may
          // still assign someone for forwarded leads. So render BOTH the
          // closed/holiday label AND the assignee on the same card.
          const closedLabel = d.is_off ? (d.off_reason || "Closed") : (d.is_sunday ? "Closed" : "");
          const personLabel = !d.is_sunday && d.person_name ? d.person_name : "";
          return (
            <div
              key={d.date}
              className="scw-card"
              style={{
                padding: 14,
                background: isClosed ? "#f4f4f4" : "#FFFFFF",
                borderTop: d.is_today ? "3px solid #5C5C5C" : "1px solid var(--scw-line)",
                minHeight: 110,
              }}
              data-testid={`dash-duty-${d.date}`}
            >
              <p className="scw-eyebrow" style={{ marginBottom: 4 }}>
                {d.weekday}{d.is_today ? " \u00b7 today" : ""}
              </p>
              <p style={{ fontSize: 13, color: "var(--scw-muted)", margin: "0 0 10px 0" }}>{d.date}</p>
              {closedLabel && (
                <p style={{ fontSize: 13, margin: "0 0 6px 0", color: "var(--scw-muted)", fontStyle: "italic" }}>
                  {closedLabel}
                </p>
              )}
              {personLabel ? (
                <p style={{ fontSize: 14, margin: 0, color: "#5C5C5C", fontWeight: 500 }}>
                  {personLabel}
                </p>
              ) : (!isClosed && (
                <p style={{ fontSize: 14, margin: 0, color: "var(--scw-muted)", fontStyle: "italic" }}>
                  Unassigned
                </p>
              ))}
            </div>
          );
        })}
      </div>

      <h2 style={{ fontSize: 18, margin: "32px 0 16px 0" }}>This month at a glance</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 32 }}>
        <ErrorBoundary label="Leads by type chart">
          <div className="scw-card" data-testid="dash-chart-type">
            <p className="scw-eyebrow" style={{ marginBottom: 12 }}>Leads by type (MTD)</p>
            {(data.month_by_type || []).every((d) => d.value === 0) ? (
              <p style={{ color: "var(--scw-muted)", fontSize: 13 }}>No data yet this month.</p>
            ) : (
              <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.month_by_type || []}>
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#5C5C5C" }} axisLine={{ stroke: "#eaeaea" }} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#9a9a9a" }} axisLine={{ stroke: "#eaeaea" }} tickLine={false} />
                    <Tooltip cursor={{ fill: "rgba(92,92,92,0.06)" }} formatter={tooltipPercentFormatter(data.month_by_type || [])} />
                    <Bar dataKey="value">
                      {(data.month_by_type || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </ErrorBoundary>
        <ErrorBoundary label="Leads by status chart">
          <div className="scw-card" data-testid="dash-chart-status">
            <p className="scw-eyebrow" style={{ marginBottom: 12 }}>Leads by status (MTD)</p>
            {(data.month_by_status || []).every((d) => d.value === 0) ? (
              <p style={{ color: "var(--scw-muted)", fontSize: 13 }}>No data yet this month.</p>
            ) : (
              <div style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.month_by_status} dataKey="value" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={2}>
                      {(data.month_by_status || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={tooltipPercentFormatter(data.month_by_status || [])} />
                    <Legend layout="horizontal" align="center" verticalAlign="bottom" iconSize={10} wrapperStyle={{ fontSize: 12, paddingTop: 10 }} formatter={legendPercentFormatter(data.month_by_status || [])} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </ErrorBoundary>
        <ErrorBoundary label="Leads by source chart">
          <div className="scw-card" data-testid="dash-chart-source">
            <p className="scw-eyebrow" style={{ marginBottom: 12 }}>Leads by source (MTD)</p>
            {!(data.month_by_source || []).length ? (
              <p style={{ color: "var(--scw-muted)", fontSize: 13 }}>No data yet this month.</p>
            ) : (
              <div style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.month_by_source} dataKey="value" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={2}>
                      {(data.month_by_source || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={tooltipPercentFormatter(data.month_by_source || [])} />
                    <Legend layout="horizontal" align="center" verticalAlign="bottom" iconSize={10} wrapperStyle={{ fontSize: 12, paddingTop: 10 }} formatter={legendPercentFormatter(data.month_by_source || [])} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </ErrorBoundary>
      </div>

      <h2 style={{ fontSize: 18, margin: "32px 0 16px 0" }}>Recent leads</h2>
      <div className="scw-card" style={{ padding: 0 }} data-testid="dash-recent-leads">
        {data.recent_leads.length === 0 ? (
          <p style={{ padding: 24, color: "var(--scw-muted)" }}>No leads yet. Customer submissions will appear here.</p>
        ) : (
          <div className="scw-table-wrap">
            <table className="scw-table">
              <thead><tr>
                <th>Timestamp</th><th>Type</th><th>Customer</th><th>Product</th><th>Duty</th><th>Status</th>
              </tr></thead>
              <tbody>
                {data.recent_leads.map((r) => (
                  <tr key={r.ID}>
                    <td>{r.Timestamp}</td>
                    <td>{r.Type}</td>
                    <td>{r.Customer_Name}</td>
                    <td>{r.Product_Watch || "\u2014"}</td>
                    <td>{r.Duty_Person || "\u2014"}</td>
                    <td><span className="scw-pill">{r.Status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <h2 style={{ fontSize: 18, margin: "32px 0 16px 0" }}>Upcoming appointments (next 7 days)</h2>
      <div className="scw-card" style={{ padding: 0 }} data-testid="dash-upcoming-appts">
        {data.upcoming_appointments.length === 0 ? (
          <p style={{ padding: 24, color: "var(--scw-muted)" }}>No upcoming appointments.</p>
        ) : (
          <div className="scw-table-wrap">
            <table className="scw-table">
              <thead><tr><th>Date</th><th>Time</th><th>Customer</th><th>Purpose</th><th>Duty</th></tr></thead>
              <tbody>
                {data.upcoming_appointments.map((r) => (
                  <tr key={r.ID}>
                    <td>{r.Appointment_Date}</td>
                    <td>{r.Appointment_Time}</td>
                    <td>{r.Customer_Name}</td>
                    <td>{r.Message}</td>
                    <td>{r.Duty_Person || "\u2014"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </AdminLayout>
  );
}
